import {
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  Text,
  Stat,
  StatNumber,
  StatHelpText,
  UnorderedList,
  ListItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  FormControl,
  Spinner
} from '@chakra-ui/react';
import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import PageContainer from '../../components/pageContainer/PageContainer';
import { createCheckoutSession } from '../../slice/pricingSlice';
import { AccountTypeEnum } from '../../utils/enums';

function Pricing() {
  const dispatch = useDispatch();
  const accountType = useSelector(state => state.app.accountType);
  const [displayProTierPurchaseScreen, setDisplayProTierPurchaseScreen] = useState(false);
  const redirectUrl = useSelector(state => state.pricing.redirectUrl);
  const [isLoading, setLoading] = useState(false);
  const [certAmt, setCertAmt] = useState(0);
  const linkRef = useRef(null);

  const makePayment = async() => {
    setLoading(true);
    await dispatch(createCheckoutSession(certAmt));
    setLoading(false);
  };

  useEffect(() => {
    if (redirectUrl) {
      linkRef.current.click();
    }
  }, [redirectUrl]);

  const professionalTierButton = (
    <Button
      margin='15px'
      color='white'
      backgroundColor='#007bff'
      onClick={() => setDisplayProTierPurchaseScreen(true)}
      disabled={accountType !== AccountTypeEnum.CERTIFICATE_ISSUER}
    >Purchase</Button>
  );
  const isPaymentButtonDisabled = certAmt <= 0 || certAmt % 10 !== 0 || certAmt > 2000;

  const updateCertAmt = val => {
    const amt = Number(val);
    if (amt >= 0 && amt <= 2000) {
      setCertAmt(amt);
    }
  };
  const getProTierCost = () => {
    return Math.ceil(certAmt / 10) * (certAmt >= 500 ? 85 : 100);
  };

  const centsToDollarString = amtCents => {
    if (amtCents === 0) {
      return '0.00';
    }
    amtCents = String(amtCents);
    return [amtCents.slice(0, amtCents.length - 2), '.', amtCents.slice(amtCents.length - 2)].join('');
  };

  const loadingModal = (
    <Modal size='md' isOpen={isLoading} isCentered>
      <ModalOverlay />
      <ModalContent background='none' boxShadow='none'>
        <ModalBody>
          <Flex alignItems='center' justifyContent='center'>
            <Spinner
              thickness='4px'
              speed='0.65s'
              emptyColor='gray.200'
              color='blue.500'
              size='xl'
            />
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  );

  const ProTierPurchaseModal = (
      <Modal size='md' isOpen={displayProTierPurchaseScreen} onClose={() => setDisplayProTierPurchaseScreen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Purchase Certificates</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Box className='custom-scrollbar'>
              <FormControl marginY='10px'>
                <Text fontSize={14} fontWeight={300}>Amount to Purchase (multiples of 10):</Text>
                <NumberInput value={certAmt} step={10} onChange={updateCertAmt} min={0} max={2000} size='md'>
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </FormControl>
              <Text>Total Cost:</Text>
              <Text>${centsToDollarString(getProTierCost())}</Text>
              <Text color='red' fontSize={14} fontWeight={300}>Please contact our team if you wish to purchase more than 2000 certificates</Text>
            </Box>
          </ModalBody>

          <ModalFooter>
            <Button disabled={isPaymentButtonDisabled} onClick={() => makePayment()} backgroundColor='#007bff' color='white' _hover={{ bg: '#0069d9', color: 'whitesmoke' }} mr={3}>
              Proceed to Payment
            </Button>
            <Button onClick={() => setDisplayProTierPurchaseScreen(false)}>Cancel</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>);

  const tiersInfo = [
    {
      tier: 'Free Tier',
      bulletPoints: [
        'All certificate issuers are given 200 certificates free of charge',
        'After the initial 200 certifiates are used up, you will have to purchase more through the Professional or Enterprise Tier'
      ]
    },
    {
      tier: 'Professional Tier',
      bulletPoints: [
        'Costs $1 per pack of 10 certficates',
        'Entitled to a 15% discount if you purchase 500 - 2000 certificates'
      ],
      paymentButton: professionalTierButton
    },
    {
      tier: 'Enterprise Tier',
      bulletPoints: [
        'If you wish to purchase a large amount of certificates and/or negotiate for a better pricing, you contact our sales team',
        'Contact information can be found on the \'Contact\' page'
      ]
    }
  ];

  const createCard = (tier, bulletPoints, paymentButton, i) => {
    return (
      <Box key={i} w='100%' h='400px'>
        <Flex flexDir='column' key={i} w='100%' h='300px' borderRadius='4px' border='1px' borderColor='blackAlpha.300' overflow='auto'>
          <Stat margin='10px'>
            <StatNumber fontWeight='600' textAlign='center'>{tier}</StatNumber>
            <Divider marginY='20px'/>
            <StatHelpText textAlign='left'>
              <Flex maxW='100%' flexDir='row'>
                <UnorderedList>
                  {bulletPoints.map((pt, i) => <ListItem key={i}>{pt}</ListItem>)}
                </UnorderedList>
              </Flex>
            </StatHelpText>
          </Stat>
          {paymentButton}
        </Flex>
      </Box>
    );
  };

  return (
    <PageContainer>
      <a ref={linkRef} href={redirectUrl} style={{ display: 'none' }}></a>
      {ProTierPurchaseModal}
      {loadingModal}
      <Text color='#377DFF' fontSize='2xl' fontWeight={600} marginY='30px'>Pricing Tiers</Text>
      <Grid gridGap='20px' h='fit-content' gridTemplateColumns='repeat(auto-fit, minmax(100px, 30%))' w='70%' justifyContent='center' justifyItems='center' alignItems='center'>
        {tiersInfo.map((info, i) => createCard(info.tier, info.bulletPoints, info.paymentButton, i))}
      </Grid>
    </PageContainer>
  );
}

export default Pricing;
